import { isMobile, isNative } from 'util/global';
import {
  CodexNoteEditor,
  CodexNotesDisplayHandlerBase,
  CodexNotesDisplayHandlerMobile,
  CodexNotesDisplayHandlerAndroid,
} from 'app/codex/notes';
import { CodexDeemphasizerEditor, CodexDeemphasizer } from 'app/codex/deemphasize';

/**
 * Map language select values to codemirror language
 * @type {Object}
 */
const LANG_CHOICES_TO_CM_MODES = {
  0: 'python',
  1: 'shell',
  2: 'clike', // c
  3: 'rust',
  4: 'qsharp', // q#
};

/**
 * @typedef {object} CodexCodeMirrorState
 * @property {CodeMirror} cm - the CodeMirror instance
 * @property {string} initialCode - the initial staff-created code
 * @property {string} initialLanguage - this initial staff-set language for the code
 */

/**
 * Load the CodeMirror JS and initialize the CodeMirror instance
 * @param {object} opts
 * @param {JQuery} opts.$editor - editor container
 * @param {JQuery} opts.$textarea - editor textarea
 * @param {JQuery} opts.$language - language dropdown
 * @param {string} opts.initialKeybinding
 * @param {string} opts.initialCode
 * @param {string} opts.initialLanguage
 * @returns {Promise}
 */
export function init(opts) {
  return new Promise(resolve => {
    import(/* webpackChunkName: "codemirror" */ 'codemirror/lib/codemirror').then(CodeMirror => {
      Promise.all([
        // syntax highlighting
        import(/* webpackChunkName: "codemirror" */ 'codemirror/mode/python/python'),
        import(/* webpackChunkName: "codemirror" */ 'codemirror/mode/shell/shell'),
        import(/* webpackChunkName: "codemirror" */ 'codemirror/mode/clike/clike'),
        import(/* webpackChunkName: "codemirror" */ 'codemirror/mode/rust/rust'),
        import(/* webpackChunkName: "codemirror" */ 'app/codex/qsharp'),

        // addons
        import(/* webpackChunkName: "codemirror" */ 'codemirror/addon/selection/active-line'),
      ]).then(() => {
        const $textareaWrapper = opts.$textarea.parents('.codex-textarea');

        opts.$editor.html('');
        $textareaWrapper.find('.CodeMirror').remove();
        opts.$editor.append($textareaWrapper);
        $textareaWrapper.removeClass('hide');

        const allowNotes = opts.$editor.parents('.codex-container').data('allow-notes-editor');

        const cm = CodeMirror.default.fromTextArea(opts.$textarea[0], {
          lineNumbers: true,
          indentUnit: 4,
          styleActiveLine: allowNotes === true,
          mode: LANG_CHOICES_TO_CM_MODES[opts.language],
          theme: 'clouds',
          extraKeys: {
            Tab: cmInstance => {
              if (cmInstance.somethingSelected()) {
                cmInstance.indentSelection('add');
                return;
              }
              cmInstance.execCommand('insertSoftTab');
            },
            'Shift-Tab': cmInstance => {
              cmInstance.indentSelection('subtract');
            },
          },
        });

        // Add a custom property for passing state info between features
        cm.brilliant = {};

        resolve({
          cm,
          initialCode: opts.initialCode,
          initialLanguage: opts.initialLanguage,
        });
      });
    });
  });
}

/**
 * Determine if the code or the language has been changed
 * @param {CodexCodeMirrorState} state
 * @param {string} lang
 * @return {boolean}
 */
export function isUnchanged(state, lang) {
  const codeUnchanged = state.cm.getValue() === state.initialCode;
  const langUnchanged = parseInt(lang, 10) === state.initialLanguage;
  return codeUnchanged && langUnchanged;
}

/**
 * Set editor to read-only
 * @param {*} state
 */
export function setReadOnly(state) {
  state.cm.setOption('readOnly', true);
}

/**
 * Handle editor change events
 * @param {CodexCodeMirrorState} state
 * @param {function} callback
 */
export function onChange(state, callback) {
  state.cm.on('change', callback);
}

/**
 * Get the raw code from the editor
 * @param {CodexCodeMirrorState} state
 * @returns {string}
 */
export function getValue(state) {
  return state.cm.getValue();
}

/**
 * Set the raw code for the editor
 * @param {CodexCodeMirrorState} state
 * @param {string} value
 */
export function setValue(state, value) {
  state.cm.brilliant.isEditing = true;
  state.cm.setValue(value);
  state.cm.brilliant.isEditing = false;
}

/**
 * Get the current cursor object
 * @param {CodexCodeMirrorState} state
 * @returns {CodeMirror.Pos} - CodeMirror Position object
 */
export function getCursor(state) {
  return state.cm.getCursor();
}

/**
 * Set the current cursor position
 * @param {CodexCodeMirrorState} state
 * @param {object} cursor
 * @param {number} cursor.line - Line number
 * @param {number} [cursor.ch] - Character number
 */
export function setCursor(state, cursor) {
  state.cm.setCursor(cursor);
}

/**
 * Set the focus on the editor
 * @param {CodexCodeMirrorState} state
 */
export function focus(state) {
  state.cm.focus();
}

/**
 * Set the current language
 * @param {CodexCodeMirrorState} state
 * @param {string} lang
 */
export function setLanguage(state, lang) {
  state.cm.setOption('mode', LANG_CHOICES_TO_CM_MODES[lang]);
}

/**
 * Initialize codex note editor
 * @param {CodexCodeMirrorState} state
 * @param {JQuery} $notesElement
 * @returns {Promise}
 */
export function initNoteEditor(state, $notesElement) {
  return new CodexNoteEditor({
    cm: state.cm,
    $notesElement,
  });
}

/**
 * Initialize codex notes display
 * @param {CodexCodeMirrorState} state
 * @returns {Promise}
 */
export function initNotes(state, $container, useAndroidHandler) {
  const opts = {
    state,
    $container,
  };

  if (useAndroidHandler) {
    return new CodexNotesDisplayHandlerAndroid(opts);
  }
  if (isMobile || isNative) {
    return new CodexNotesDisplayHandlerMobile(opts);
  }

  return new CodexNotesDisplayHandlerBase(opts);
}

export function initDeemphasizeEditor({ state, $deemphasizeField, $deemphasizeContainer }) {
  return new CodexDeemphasizerEditor({
    cm: state.cm,
    $deemphasizeField,
    $deemphasizeContainer,
  });
}

export function initDeemphasize({ state, data, envId, initialCode }) {
  return new CodexDeemphasizer({
    cm: state.cm,
    data,
    envId,
    initialCode,
  });
}
